/*
 * Groply product.js (product builder). Progressive enhancement for
 * sections/main-product.liquid. Loaded deferred on every page, so it does
 * nothing unless [data-pdp] markup is present. Without it the product form
 * posts normally to /cart/add.
 *
 * Markup hooks (inside [data-pdp]):
 *   script[data-product-json]   {"variants":[{id,title,options,available,sku,barcode,barcodeLabel,mediaId,priceHtml}]}
 *   form[data-pdp-form]         the standard product form
 *   select[data-pdp-variant]    <select name="id"> (only when the product has more than one variant;
 *                               hidden with JS on, kept in sync with the option buttons)
 *   [data-pdp-options]          option buttons: fieldset[data-pdp-option="<index>"] with
 *                               input[data-pdp-option-input] radios, [data-pdp-option-current]
 *                               (selected value text) and [data-pdp-option-note] (sold out note)
 *   .pdp-option__swatch         colour square; data-swatch = value name, resolved to a CSS colour
 *                               here unless Shopify gave one (data-swatch-set)
 *   [data-pdp-media-index]      "1 / 4" image counter on the gallery well, follows gallery:change
 *   [data-pdp-price]            price block (innerHTML swapped with the pre-rendered price snippet)
 *   [data-pdp-stock]            availability line (+ [data-pdp-stock-text], data-label-in/out/unavailable)
 *   [data-pdp-add]              add to cart button (+ [data-pdp-add-text], data-label-add/sold-out/unavailable)
 *   [data-pdp-dynamic]          dynamic checkout wrapper (hidden when the variant is unavailable)
 *   [data-pdp-sku-row] / [data-pdp-sku], [data-pdp-barcode-row] / [data-pdp-barcode-label] / [data-pdp-barcode]
 *                               specs table rows (tr), hidden when the variant has no value
 *   [data-pdp-status]           live region for add to cart feedback (data-cart-url, data-added, data-view-cart, data-error)
 *   [data-gallery]              theme.js gallery; selected with the gallery:select event
 *
 * Gallery thumbnails and swipe syncing are handled by theme.js ([data-gallery],
 * CONTRACT 6.6); this file only asks the gallery to show a variant's image.
 * After an AJAX add it updates every [data-cart-count] and emits cart:updated.
 * Public: window.GroplyProduct.init(context).
 */
(function () {
  'use strict';

  if (window.GroplyProduct) return;

  var doc = document;

  function all(selector, context) {
    return Array.prototype.slice.call((context || doc).querySelectorAll(selector));
  }

  function readJSON(node) {
    if (!node) return null;
    try {
      return JSON.parse(node.textContent || 'null');
    } catch (error) {
      return null;
    }
  }

  function hasValue(value) {
    return value != null && String(value).replace(/\s+/g, '') !== '';
  }

  function setText(node, value) {
    if (node) node.textContent = value == null ? '' : String(value);
  }

  function emit(target, name, detail) {
    if (!target || typeof window.CustomEvent !== 'function') return;
    try {
      target.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: detail }));
    } catch (error) {
      /* ignore */
    }
  }

  /* ---------------------------------------------------------------------
   * Cart count (header markup is owned by chrome: [data-cart-count])
   * ------------------------------------------------------------------- */
  function refreshCartCount(cartUrl) {
    if (!window.fetch) return;
    var url = (cartUrl || '/cart').split('?')[0].replace(/\/$/, '') + '.js';
    window.fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (response) {
        if (!response.ok) throw new Error('cart ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        if (!cart || typeof cart.item_count !== 'number') return;
        all('[data-cart-count]').forEach(function (node) {
          node.textContent = String(cart.item_count);
          node.hidden = cart.item_count === 0;
        });
        emit(doc, 'cart:updated', { cart: cart });
      })
      .catch(function () {
        /* The item is in the cart; the count updates on the next page load. */
      });
  }

  /* ---------------------------------------------------------------------
   * Colour swatches: desk accessory finish names to CSS colours (names CSS
   * does not know, or knows as a much louder shade)
   * ------------------------------------------------------------------- */
  var SWATCH_EXTRA = {
    clear: 'transparent',
    frosted: '#E6ECEF',
    frost: '#E6ECEF',
    smoke: '#6E747A',
    natural: '#D9C3A0',
    kraft: '#B98E5C',
    bamboo: '#D2B47C',
    walnut: '#5C3D2E',
    oak: '#B98A55',
    cork: '#C49A6C',
    sage: '#9AAE8C',
    mint: '#B8E0D2',
    blush: '#E8B9B0',
    lavender: '#B9A7D6',
    teal: '#1F7A78',
    navy: '#1F2A44',
    slate: '#55616E',
    charcoal: '#36454F',
    graphite: '#41424C',
    gunmetal: '#4A4E55',
    spacegray: '#5F6368',
    silver: '#B8BCC2',
    gold: '#C9A227',
    rosegold: '#C99A8C',
    copper: '#B87333',
    brass: '#B5A642',
    orange: '#E0661B',
    purple: '#5B3A99',
    pink: '#E46A9A',
    red: '#C1272D',
    blue: '#2E5585',
    green: '#2F7A4A',
    yellow: '#F2C94C',
    white: '#FFFFFF',
    black: '#1A1A1A'
  };

  function cssColor(word) {
    if (!word) return null;
    if (Object.prototype.hasOwnProperty.call(SWATCH_EXTRA, word)) return SWATCH_EXTRA[word];
    try {
      if (window.CSS && typeof window.CSS.supports === 'function' && window.CSS.supports('color', word)) return word;
    } catch (error) {
      /* ignore */
    }
    return null;
  }

  // "Royal Blue" -> royalblue, else blue; "Black/White" -> two colours.
  function resolveColour(name) {
    var parts = String(name || '').toLowerCase().split(/\s*(?:\/|&|\+|,|\band\b)\s*/);
    var colours = [];
    parts.forEach(function (part) {
      var words = part.split(/[^a-z]+/).filter(Boolean);
      if (!words.length) return;
      var found = cssColor(words.join(''));
      for (var i = words.length - 1; !found && i >= 0; i--) found = cssColor(words[i]);
      if (found) colours.push(found);
    });
    return colours.slice(0, 2);
  }

  function paintSwatches(context) {
    all('.pdp-option__swatch[data-swatch]', context).forEach(function (node) {
      if (node.hasAttribute('data-swatch-set')) return;
      var colours = resolveColour(node.getAttribute('data-swatch'));
      if (!colours.length) {
        node.hidden = true;
        return;
      }
      node.style.setProperty('--pdp-swatch', colours[0]);
      node.style.setProperty('--pdp-swatch-2', colours[1] || colours[0]);
      if (colours[0] === 'transparent' && colours.length === 1) node.classList.add('is-clear');
    });
  }

  /* ---------------------------------------------------------------------
   * One product section
   * ------------------------------------------------------------------- */
  function setupProduct(root) {
    if (!root || root.__hmProduct) return;
    root.__hmProduct = true;

    var data = readJSON(root.querySelector('script[data-product-json]'));
    var variants = data && Array.isArray(data.variants) ? data.variants : [];
    var byId = {};
    variants.forEach(function (variant) {
      if (variant && variant.id != null) byId[String(variant.id)] = variant;
    });

    var form = root.querySelector('form[data-pdp-form]');
    var select = root.querySelector('select[data-pdp-variant]');
    var ui = {
      price: root.querySelector('[data-pdp-price]'),
      stock: root.querySelector('[data-pdp-stock]'),
      stockText: root.querySelector('[data-pdp-stock-text]'),
      add: root.querySelector('[data-pdp-add]'),
      addText: root.querySelector('[data-pdp-add-text]'),
      dynamic: root.querySelector('[data-pdp-dynamic]'),
      skuRow: root.querySelector('[data-pdp-sku-row]'),
      sku: root.querySelector('[data-pdp-sku]'),
      barcodeRow: root.querySelector('[data-pdp-barcode-row]'),
      barcodeLabel: root.querySelector('[data-pdp-barcode-label]'),
      barcode: root.querySelector('[data-pdp-barcode]'),
      status: root.querySelector('[data-pdp-status]'),
      gallery: root.querySelector('[data-gallery]')
    };

    function clearStatus() {
      if (!ui.status) return;
      ui.status.textContent = '';
      ui.status.className = 'pdp-form__status';
    }

    function showStatus(kind, message, withCartLink) {
      if (!ui.status) return;
      clearStatus();
      ui.status.className = 'pdp-form__status notice ' + (kind === 'error' ? 'notice--error' : 'notice--success');
      var text = doc.createElement('span');
      text.textContent = message;
      ui.status.appendChild(text);
      if (withCartLink) {
        var link = doc.createElement('a');
        link.className = 'link';
        link.href = ui.status.getAttribute('data-cart-url') || '/cart';
        link.textContent = ui.status.getAttribute('data-view-cart') || '';
        ui.status.appendChild(link);
      }
    }

    function setRow(row, valueNode, value) {
      if (!row) return;
      var show = hasValue(value);
      row.hidden = !show;
      if (show) setText(valueNode, value);
    }

    function render(variant, options) {
      options = options || {};
      var available = !!(variant && variant.available);
      var state = variant ? (available ? 'in' : 'out') : 'unavailable';

      if (ui.price && variant && typeof variant.priceHtml === 'string') {
        ui.price.innerHTML = variant.priceHtml;
      }

      if (ui.stock) {
        ui.stock.classList.toggle('pdp-stock--in', available);
        ui.stock.classList.toggle('pdp-stock--out', !available);
        var stockLabel = ui.stock.getAttribute('data-label-' + state);
        if (stockLabel) setText(ui.stockText, stockLabel);
      }

      if (ui.add) {
        ui.add.disabled = !available;
        var addKey = state === 'in' ? 'data-label-add' : (state === 'out' ? 'data-label-sold-out' : 'data-label-unavailable');
        var addLabel = ui.add.getAttribute(addKey);
        if (addLabel) setText(ui.addText, addLabel);
      }

      if (ui.dynamic) ui.dynamic.hidden = !available;

      setRow(ui.skuRow, ui.sku, variant ? variant.sku : null);
      setRow(ui.barcodeRow, ui.barcode, variant ? variant.barcode : null);
      if (variant && hasValue(variant.barcodeLabel)) setText(ui.barcodeLabel, variant.barcodeLabel);

      if (variant && options.syncGallery && variant.mediaId != null && ui.gallery) {
        var themeApi = window.Groply;
        if (themeApi && typeof themeApi.gallerySelect === 'function') {
          themeApi.gallerySelect(ui.gallery, variant.mediaId);
        } else {
          emit(ui.gallery, 'gallery:select', { mediaId: variant.mediaId });
        }
      }

      if (variant && options.updateUrl && window.history && typeof window.history.replaceState === 'function') {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState(window.history.state, '', url.toString());
        } catch (error) {
          /* ignore: URL stays as it was */
        }
      }

      clearStatus();
      emit(root, 'product:variant-change', { variant: variant || null });
    }

    /* Option buttons ------------------------------------------------- */
    var optionsRoot = root.querySelector('[data-pdp-options]');
    var fieldsets = optionsRoot ? all('[data-pdp-option]', optionsRoot) : [];

    function selectedOptions() {
      return fieldsets.map(function (fieldset) {
        var checked = fieldset.querySelector('[data-pdp-option-input]:checked');
        return checked ? checked.value : null;
      });
    }

    function findVariant(values) {
      for (var i = 0; i < variants.length; i++) {
        var opts = variants[i].options || [];
        var match = true;
        for (var j = 0; j < values.length; j++) {
          if (values[j] !== opts[j]) { match = false; break; }
        }
        if (match) return variants[i];
      }
      return null;
    }

    // Mark every value that is sold out (or has no variant) given the other
    // options as currently chosen, and update the "Color  Navy" legend text.
    function markOptions(values) {
      fieldsets.forEach(function (fieldset, index) {
        setText(fieldset.querySelector('[data-pdp-option-current]'), values[index] || '');
        all('[data-pdp-option-input]', fieldset).forEach(function (input) {
          var probe = values.slice();
          probe[index] = input.value;
          var variant = findVariant(probe);
          var state = !variant ? 'unavailable' : (variant.available ? '' : 'out');
          var label = fieldset.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          label.classList.toggle('is-out', state === 'out');
          label.classList.toggle('is-unavailable', state === 'unavailable');
          var text = state && ui.stock ? ui.stock.getAttribute('data-label-' + state) : '';
          setText(label.querySelector('[data-pdp-option-note]'), text ? ', ' + text : '');
        });
      });
    }

    function onOptionChange() {
      var values = selectedOptions();
      var variant = findVariant(values);
      if (select && variant) select.value = String(variant.id);
      markOptions(values);
      render(variant, { updateUrl: true, syncGallery: true });
    }

    if (fieldsets.length) {
      paintSwatches(optionsRoot);
      optionsRoot.addEventListener('change', function (event) {
        var target = event.target;
        if (target && target.hasAttribute && target.hasAttribute('data-pdp-option-input')) onOptionChange();
      });
    }

    if (select) {
      select.addEventListener('change', function () {
        var variant = byId[select.value] || null;
        if (variant && fieldsets.length) {
          fieldsets.forEach(function (fieldset, index) {
            all('[data-pdp-option-input]', fieldset).forEach(function (input) {
              input.checked = input.value === (variant.options || [])[index];
            });
          });
          markOptions(selectedOptions());
        }
        render(variant, { updateUrl: true, syncGallery: true });
      });
    }

    // The browser may restore a different choice (reload, back/forward).
    var initial = root.getAttribute('data-current-variant');
    if (fieldsets.length) {
      var restored = findVariant(selectedOptions());
      if (restored && String(restored.id) !== initial) onOptionChange();
      else markOptions(selectedOptions());
    } else if (select && select.value && select.value !== initial) {
      render(byId[select.value] || null, { updateUrl: true, syncGallery: true });
    }

    /* Image counter ("2 / 5") ------------------------------------------ */
    var counter = root.querySelector('[data-pdp-media-index]');
    if (counter && ui.gallery) {
      ui.gallery.addEventListener('gallery:change', function (event) {
        var id = event.detail && event.detail.mediaId != null ? String(event.detail.mediaId) : null;
        if (!id) return;
        var items = all('[data-gallery-item]', ui.gallery);
        for (var i = 0; i < items.length; i++) {
          if (items[i].getAttribute('data-media-id') === id) {
            setText(counter, i + 1);
            break;
          }
        }
      });
    }

    if (form) {
      form.addEventListener('input', function (event) {
        if (event.target && event.target.name === 'quantity') clearStatus();
      });
      bindAjaxAdd(form);
    }

    /* Optional AJAX add to cart. Validation errors (422) are shown inline;
       any other failure falls back to the normal form post. */
    function bindAjaxAdd(productForm) {
      if (!window.fetch || !window.FormData) return;

      productForm.addEventListener('submit', function (event) {
        if (event.defaultPrevented) return;
        var action = (productForm.getAttribute('action') || '').split('?')[0];
        if (!action) return;
        event.preventDefault();
        if (productForm.__hmBusy || (ui.add && ui.add.disabled)) return;

        productForm.__hmBusy = true;
        if (ui.add) {
          ui.add.classList.add('is-loading');
          ui.add.setAttribute('aria-busy', 'true');
        }
        clearStatus();

        function done() {
          productForm.__hmBusy = false;
          if (ui.add) {
            ui.add.classList.remove('is-loading');
            ui.add.removeAttribute('aria-busy');
          }
        }

        function postNormally() {
          done();
          HTMLFormElement.prototype.submit.call(productForm);
        }

        window.fetch(action.replace(/\/$/, '') + '.js', {
          method: 'POST',
          body: new FormData(productForm),
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
        })
          .then(function (response) {
            return response.json().then(
              function (json) { return { ok: response.ok, status: response.status, json: json }; },
              function () { return { ok: response.ok, status: response.status, json: null }; }
            );
          })
          .then(function (result) {
            if (result.ok) {
              done();
              showStatus('success', ui.status ? ui.status.getAttribute('data-added') : '', true);
              refreshCartCount(ui.status ? ui.status.getAttribute('data-cart-url') : null);
              return;
            }
            if (result.status >= 400 && result.status < 500 && result.json) {
              done();
              var message = result.json.description || result.json.message;
              if (typeof message !== 'string' || !message) message = ui.status ? ui.status.getAttribute('data-error') : '';
              showStatus('error', message, false);
              return;
            }
            postNormally();
          }, postNormally);
      });
    }
  }

  function init(context) {
    all('[data-pdp]', context).forEach(setupProduct);
  }

  function boot() {
    init(doc);
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  // Theme editor: re-run for a section that was re-rendered.
  doc.addEventListener('shopify:section:load', function (event) {
    var target = event.target;
    if (!target || !target.querySelectorAll) return;
    if (target.matches && target.matches('[data-pdp]')) setupProduct(target);
    init(target);
  });

  window.GroplyProduct = { init: init };
})();
